import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@welldev-group/rcl-icon';
import Card from '@welldev-group/rcl-card';
import { Header, Text as Typography } from '@welldev-group/rcl-typography';
import { BreadcrumbItem, Breadcrumbs, I18nText, Stepper, Amount } from '@welldev-group/react-comp-lib';
import Alert from '@welldev-group/rcl-alert';
import bank from 'affiliateImages/bank.svg';
import DateTime from '../DateTime';
import ShowBankDetails from './ShowBankDetails';
import ContactPage from '../../../public/components/ContactPage';

const TransactionDetails = ({
  title,
  transactionType,
  lineItem,
  timestamp,
  balanceChange,
  urls,
  notification,
  signInRoutes,
  recaptchaKey,
  recaptchaWidgetSrc,
  cancelInvoiceModal,
  pdf,
  reference,
  reason,
}) => {
  const dividerContent = <div className="billing__header--divider" />;

  const currencyName = balanceChange && balanceChange.currency;
  const WireTransferPaymentType = 'WireTransferPayment';
  const WelltravelBookingTransactionType = 'WelltravelBookingTransaction';
  const stepsContainer = [
    { stepName: <I18nText id="billing.stepper.first_step" /> },
    {
      stepName: (
        <I18nText
          id={`billing.stepper.${
            transactionType === WireTransferPaymentType ? 'second_step' : 'second_step_card'
          }`}
        />
      ),
    },
    { stepName: <I18nText id="billing.stepper.third_step" /> },
  ];

  const invoiceStepsContainer = [
    { stepName: <I18nText id="billing.stepper.first_step" /> },
    { stepName: <I18nText id="billing.stepper.second_step" /> },
    { stepName: <I18nText id="billing.stepper.third_step" /> },
  ];

  const walletStepsContainer = [
    { stepName: <I18nText id="billing.stepper.wallet.first_step" /> },
    { stepName: <I18nText id="billing.stepper.third_step" /> },
  ];

  const remarkType = {
    cancellation: 'billing.invoices.remarks.cancellation',
    cancellation_penalty: 'billing.invoices.remarks.cancellation_penalty',
    price_decreased: 'billing.invoices.remarks.price_decreased',
    price_increased: 'billing.invoices.remarks.price_increased',
    refund: 'billing.invoices.remarks.refund',
    release: 'billing.invoices.remarks.release',
    booking: 'billing.invoices.remarks.booking',
    subscription_charge: 'billing.invoices.remarks.subscription_charge',
  };

  const billingReason = {
    cancellation: 'billing.transactions.billing_reason.cancellation',
    cancellation_penalty: 'billing.transactions.billing_reason.cancellation_penalty',
    price_decreased: 'billing.transactions.billing_reason.price_decreased',
    price_increased: 'billing.transactions.billing_reason.price_increased',
    refund: 'billing.transactions.billing_reason.refund',
    release: 'billing.transactions.billing_reason.release',
    booking: 'billing.transactions.billing_reason.booking',
  };

  const walletTransactionsContainer = (
    <>
      <div className="billing-invoice__timestamp__wallet col-grid col-bleed-y">
        {reference?.workspace && (
          <span>
            <div className="billing-invoice__timestamp__title">
              <I18nText id="billing.invoices.associated_workspace" />
            </div>
            <Header level={5} weight="bold">
              {`${reference?.workspace?.name} [${reference?.workspace?.identifier}]`}
            </Header>
          </span>
        )}
        {reference?.workspace && (
          <span>
            <div className="billing-invoice__timestamp__title">
              <I18nText id="billing.invoices.order" />
            </div>
            <Header level={5} weight="bold">
              {reference?.workspace?.orderReference}
            </Header>
          </span>
        )}
        <span>
          <div className="billing-invoice__timestamp__title">
            <I18nText id="billing.invoices.date" />
          </div>
          <Header level={5} weight="bold">
            {timestamp && <DateTime dateTime={timestamp} format="long" />}
          </Header>
        </span>
        {remarkType[reason] && !notification && (
          <span>
            <div className="billing-invoice__timestamp__title">
              <I18nText id="billing.invoices.remarks.title" />
            </div>
            <Header level={5} weight="bold">
              <I18nText id={remarkType[reason]} />
            </Header>
          </span>
        )}
      </div>
      {dividerContent}
    </>
  );

  const generalTransactionsContainer = (
    <div className="billing-invoice__timestamp col-grid col-bleed-y">
      <span>
        <Icon name="time" />
      </span>
      <span>{timestamp && <DateTime dateTime={timestamp} format="long" />}</span>
    </div>
  );

  return (
    <div className="container-full">
      <div className="billing__header">
        <div className="billing__header-title">
          <I18nText id="billing.transactions.title" />
        </div>
        <div className="billing__breadcrumbs">
          <Breadcrumbs>
            <BreadcrumbItem linkUrl={urls.billing}>
              <I18nText id="billing.header" />
            </BreadcrumbItem>
            <BreadcrumbItem linkUrl={`${urls.transactions}`}>
              <I18nText id="billing.transactions.title" />
            </BreadcrumbItem>
            <BreadcrumbItem linkUrl={urls.thisEntity}>{title}</BreadcrumbItem>
          </Breadcrumbs>
        </div>
      </div>
      {notification ? (
        <div className="billing__stepper-wrapper">
          <Stepper
            steps={reason ? walletStepsContainer : invoiceStepsContainer}
            activeStepIndex={1}
            showActionButtons={false}
            showStepTitle={false}
          />
        </div>
      ) : (
        <div className="billing__stepper-wrapper">
          <Stepper
            steps={
              transactionType === WelltravelBookingTransactionType
                ? walletStepsContainer
                : stepsContainer
            }
            activeStepIndex={transactionType === WelltravelBookingTransactionType ? 2 : 3}
            showActionButtons={false}
            showStepTitle={false}
          />
        </div>
      )}
      <Card version="v2">
        <div className="grid">
          <div className="col-lg-6 col-md-6 col-12 col-bleed-y">
            <Header level={3} weight="bold">
              {title}
            </Header>
          </div>
          <div className="col-lg-6 col-md-6 col-12 col-bleed-y billing-invoice-cancel-modal__button">
            {pdf}
            {cancelInvoiceModal}
          </div>
          <div className="billing-invoice__timestamp__content">
            {transactionType === WelltravelBookingTransactionType || (notification && reason)
              ? walletTransactionsContainer
              : generalTransactionsContainer}
          </div>
          <div className="col-12 col-bleed col-grid direction-row">
            {notification && !reason ? (
              <div className="col-lg-6 col-md-6 col-12 col-grid">
                <div>
                  <Typography level={1} weight="medium">
                    <I18nText id="billing.invoices.account_details_header" />
                  </Typography>
                  <Typography level={1} className="billing-invoice__details-text">
                    <span>
                      <I18nText id="billing.invoices.account_details_sub_header" />
                    </span>
                    <span className="billing-invoice__details-link-text contact-us__wrapper">
                      <ContactPage
                        linkText={<I18nText id="billing.invoices.account_details_link" />}
                        signInUrl={signInRoutes.signInUrl}
                        recaptchaKey={recaptchaKey}
                        recaptchaWidgetSrc={recaptchaWidgetSrc}
                        buttonClassName="billing-invoice__details-link-text-button"
                      />
                    </span>
                  </Typography>
                </div>
                <div className="billing-invoice__details-bank-info flex-footer">
                  <div>
                    <img src={bank} alt="Bank" />
                  </div>
                  <div className="billing-invoice__details-bank-info-details">
                    <ShowBankDetails currency={currencyName} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-lg-6 col-md-6 col-12">
                <Header level={5} weight="bold">
                  <I18nText id="billing.invoices.billing_payment" />
                </Header>
                <div className="billing-invoice__billing-wrapper billing-invoice__billing-wrapper--info">
                  <div className="billing-invoice__billing-items">
                    <div className="billing-invoice__billing-items-text">
                      <I18nText id="billing.invoices.billing_initial" />
                    </div>
                    <div className="billing-invoice__billing-items-amount">
                      {balanceChange && (
                        <Amount
                          locale="de-CH"
                          value={Math.abs(balanceChange.before)}
                          currency={balanceChange.currency}
                          minFractionDigit={2}
                        />
                      )}
                    </div>
                  </div>
                  <div className="billing-invoice__billing-items">
                    <div className="billing-invoice__billing-items-text">{title}</div>
                    <div className="billing-invoice__billing-items-amount">
                      {balanceChange && (
                        <Amount
                          locale="de-CH"
                          value={Math.abs(balanceChange.amount)}
                          currency={balanceChange.currency}
                          minFractionDigit={2}
                        />
                      )}
                    </div>
                  </div>
                  <div className="billing-invoice__billing-items">
                    <div className="billing-invoice__billing-items-text--bold">
                      <I18nText id="billing.invoices.billing_new" />
                    </div>
                    <div className="billing-invoice__billing-items-amount">
                      {balanceChange && (
                        <Amount
                          locale="de-CH"
                          value={Math.abs(balanceChange.after)}
                          currency={balanceChange.currency}
                          minFractionDigit={2}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="col-lg-6 col-md-6 col-12">
              <Header level={5} weight="bold">
                <I18nText id="billing.invoices.billing" />
              </Header>
              <div className="billing-invoice__billing-wrapper billing-invoice__billing-wrapper--summary">
                <div className="billing-invoice__billing-items">
                  <div className="billing-invoice__billing-items-text">
                    <span>
                      {transactionType === WelltravelBookingTransactionType ? (
                        <I18nText id={billingReason[reason]} />
                      ) : (
                        lineItem
                      )}
                    </span>
                    {transactionType !== WelltravelBookingTransactionType && !reason && (
                      <>
                        <span>(</span>
                        <span className="billing-invoice__details-currency">{currencyName}</span>
                        <span>)</span>
                      </>
                    )}
                  </div>
                  <div className="billing-invoice__billing-items-amount">
                    {balanceChange && (
                      <Amount
                        locale="de-CH"
                        value={Math.abs(balanceChange.amount)}
                        currency={balanceChange.currency}
                        minFractionDigit={2}
                      />
                    )}
                  </div>
                </div>
                <div className="billing-invoice__billing-items">
                  <div className="billing-invoice__billing-items-text">
                    <span>
                      <I18nText id="billing.invoices.total" />{' '}
                    </span>
                    <span>(</span>
                    <span className="billing-invoice__details-currency">{currencyName}</span>
                    <span>)</span>
                  </div>
                  <div className="billing-invoice__billing-items-amount--big">
                    {balanceChange && (
                      <Amount
                        locale="de-CH"
                        value={Math.abs(balanceChange.amount)}
                        currency={balanceChange.currency}
                        minFractionDigit={2}
                      />
                    )}
                  </div>
                </div>
                <Alert
                  className="billing-invoice__notification"
                  isVisible={!!notification && reason !== 'subscription_charge'}
                  type="warning"
                  hideClose={true}
                >
                  {notification}
                </Alert>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

TransactionDetails.defaultProps = {
  timestamp: null,
  balanceChange: null,
  notification: null,
  lineItem: null,
  cancelInvoiceModal: null,
  reference: null,
};

TransactionDetails.propTypes = {
  title: PropTypes.node.isRequired,
  timestamp: PropTypes.string,
  lineItem: PropTypes.node,
  balanceChange: PropTypes.shape({
    before: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
    after: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }),
  notification: PropTypes.node,
  urls: PropTypes.shape({
    billing: PropTypes.string.isRequired,
    transactions: PropTypes.string.isRequired,
    thisEntity: PropTypes.string.isRequired,
  }).isRequired,
  signInRoutes: PropTypes.shape({
    signInUrl: PropTypes.string.isRequired,
  }).isRequired,
  recaptchaKey: PropTypes.string.isRequired,
  recaptchaWidgetSrc: PropTypes.string.isRequired,
  cancelInvoiceModal: PropTypes.node,
  pdf: PropTypes.node.isRequired,
  transactionType: PropTypes.string.isRequired,
  reference: PropTypes.shape({
    workspace: PropTypes.shape({
      identifier: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      orderReference: PropTypes.string.isRequired,
    }),
  }),
  reason: PropTypes.string.isRequired,
};

export default TransactionDetails;
